import { Box, Button, Dialog, Link, MenuItem, TextField } from "@material-ui/core"
import { equals, remove } from "ramda"
import { useEffect, useMemo, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"
import { useForm } from "react-hook-form"
import type { Path, PathValue } from "react-hook-form"
import { connect } from "react-redux"

import { Autocomplete } from "@material-ui/lab"
import produce from "immer"
import { MapAttachmentEditDialog, MapViewer, MapViewerControls } from ".."
import {
  MapViewerPanel,
  metadataAddAttachment,
  metadataFetchCategories,
  metadataMoveAttachment,
  metadataUpdateAttachment,
  metadataUpdateAttachments,
  searchAssets
} from "../../../redux"
import type {
  AppState,
  AppThunkDispatch,
  Asset,
  Attachment,
  Chapter,
  ILatLng,
  Licence,
  Metadata,
  MetadataActions,
  Region,
  Theme
} from "../../../redux"
import { convertLatLngInterfaceToLatLng } from "../../../utils"
import { Body1, ErrorBoundary, Header4, TextInputField } from "../../atoms"
import { CheckboxControl } from "../../atoms/form-controls"
import {
  AttachmentLocationChanger,
  FormValidationErrors,
  LicenceField,
  MapActionsPanel,
  MapAttachementSidePanel,
  MapDrawing,
  MetadataIndexFormPreview,
  SelectorComponent
} from "../../molecules"

import "./map-metadata-manger.styles.scss"
import { useTranslation } from "react-i18next"

export interface IMapMetadataManagerFormProps {
  metadata: Metadata
  chapters: Chapter[]
  regions: Region[]
  regionThemes: Theme[]
  chapterThemes: Theme[]
  licences: Licence[]
  assets: Asset[]
  onLicenceIdsChanged: (licenceIds: string[]) => void
  onChapterChange: (chapterId: string, licenceIds: string[]) => void
  onRegionChange: (regionId: string, licenceIDs: string[]) => void
  saveMetadata: (metadata: Metadata) => void
}

export interface IMapMetadataManagerStateProps {
  showControls: boolean
  showAttachmentPanel: boolean
  categories: string[]
  addAttachment: (attachment: Attachment, file?: File) => void
  updateAttachment: (attachment: Attachment, file?: File) => void
  updateAttachments: (attachments: Attachment[]) => void
  moveAttachment: (attachment: Attachment) => void
  searchAssets: (searchValue: string) => void
  fetchCategories: () => void
}

function MapMetadataManagerFormView(props: IMapMetadataManagerFormProps & IMapMetadataManagerStateProps): ReactElement {
  const {
    metadata,
    chapters,
    regions,
    regionThemes,
    chapterThemes,
    licences,
    assets,
    showControls,
    showAttachmentPanel,
    onLicenceIdsChanged,
    onChapterChange,
    onRegionChange,
    saveMetadata,
    addAttachment,
    updateAttachment,
    updateAttachments,
    moveAttachment,
    searchAssets,
    fetchCategories,
    categories
  } = props

  const {
    control,
    formState: { errors, isDirty, submitCount },
    setValue,
    getValues,
    reset,
    handleSubmit,
    watch,
    setError,
    clearErrors
  } = useForm<Metadata>({
    defaultValues: metadata,
    mode: "onChange",
    reValidateMode: "onChange"
  })

  const { t } = useTranslation("contentManager")

  const [mapDialogOpen, setMapDialogOpen] = useState<boolean>(false)
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(
    assets.find((x) => x.id === getValues("assetId"))
  )
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const hasValidIndexSpot: boolean = useMemo<boolean>(() => {
    const { chapterId, topicChapterId, regionId, themeId, chapterThemeId, showUnderChapter, showUnderRegion } =
      getValues()

    const assignedToChapter: boolean = chapterId !== undefined && chapterId !== " " && showUnderChapter
    const assignedToRegion: boolean = !!regionId && regionId !== " " && showUnderRegion
    const assignedToTheme: boolean = !!themeId && themeId !== " "
    const assignedToChapterTheme: boolean = !!chapterThemeId && chapterThemeId !== " "
    const assignedToTopicChapter: boolean =
      topicChapterId !== undefined &&
      topicChapterId !== " " &&
      chapterId !== undefined &&
      chapterId !== " " &&
      topicChapterId !== chapterId

    return assignedToChapter || assignedToRegion || assignedToTheme || assignedToChapterTheme || assignedToTopicChapter
  }, [getValues()])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchCategories()
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    const chapterId: string | undefined = getValues("chapterId")
    if (chapters.length > 0 && (!chapterId || chapterId.length === 0)) {
      setValue("chapterId", chapters[0].id, { shouldValidate: true })
    }
  }, [chapters])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (submitCount > 0 && isDirty) {
      selectedAsset
        ? clearErrors("assetId")
        : setError("assetId", {
            type: "manual",
            message: t("metadata.formFields.assetError")
          })
    }
  }, [selectedAsset, submitCount])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (assets.length > 0 && !selectedAsset) {
      setSelectedAsset(assets.find((x) => x.id === getValues("assetId")))
    }
  }, [assets])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (metadata && !formSynced()) {
      if (metadata.attachments.length !== getValues().attachments.length) {
        reset({
          ...getValues(),
          attachments: metadata.attachments
        })
      } else {
        reset(metadata)
        setSelectedAsset(assets.find((x) => x.id === metadata.assetId))
      }
    }
  }, [metadata])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    const licenceIds: string[] = getValues("licenceIds")
    if (licenceIds !== metadata.licenceIds) {
      reset({
        ...getValues(),
        showUnderChapter: false,
        showUnderRegion: false
      })
    }
    if (licenceIds.length > 0) {
      onLicenceIdsChanged(licenceIds)
    }
  }, [watch("licenceIds")])

  function formSynced(): boolean {
    return equals(getValues(), metadata)
  }

  function onSubmit(data: Metadata): void {
    saveMetadata({ ...data, assetId: selectedAsset?.id as string })
  }

  function validateTopicChapter(topicChapterId?: PathValue<Metadata, Path<Metadata>>) {
    if (topicChapterId === undefined) return true
    if (typeof topicChapterId === "string") {
      const chapterId: string | undefined = getValues().chapterId
      return topicChapterId !== chapterId
    }
    return false
  }

  function chapterIdChanged(
    changeHandler: (event: ChangeEvent<HTMLInputElement>) => void
  ): (event: ChangeEvent<HTMLInputElement>) => void {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const chapterId: string = event.target.value
      reset({
        ...getValues(),
        regionId: " ",
        themeId: " ",
        chapterThemeId: " ",
        showUnderChapter: false
      })
      onChapterChange(chapterId, getValues("licenceIds"))
      changeHandler(event)
    }
  }

  function regionIdChanged(
    changeHandler: (event: ChangeEvent<HTMLInputElement>) => void
  ): (event: ChangeEvent<HTMLInputElement>) => void {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const regionId: string = event.target.value
      reset({ ...getValues(), themeId: " ", showUnderRegion: false })
      onRegionChange(regionId, getValues("licenceIds"))
      changeHandler(event)
    }
  }

  function removePinLocation(attachment: Attachment): void {
    if (!metadata) return

    const newAttachments: Attachment[] = metadata.attachments.map((a: Attachment) => {
      if (a.order === attachment.order) {
        return produce(attachment, (draft: Attachment) => {
          draft.pointOnMap = undefined
        })
      }
      return a
    })

    updateAttachments(newAttachments)
  }

  function removeAttachment(attachment: Attachment): void {
    if (!metadata) return
    const index: number = metadata.attachments.indexOf(attachment)
    updateAttachments(remove(index, 1, metadata.attachments))
  }

  function hasLicenceSelected(): boolean {
    return getValues("licenceIds").length > 0
  }

  function onAssetSelect(_: ChangeEvent<{}>, value: Asset | null): void {
    if (!value) return
    setSelectedAsset(value)
  }

  function onAssetInputChange(_: ChangeEvent<{}>, value: string): void {
    if (value === selectedAsset?.assetName) searchAssets("")
    else searchAssets(value)
  }

  function getChapterName(): string | undefined {
    return chapters.find((chapter: Chapter) => chapter.id === getValues().chapterId)?.name
  }

  function getTopicChapterName(): string | undefined {
    return chapters.find((chapter: Chapter) => chapter.id === getValues().topicChapterId)?.name
  }

  function getRegionName(): string | undefined {
    return regions.find((region: Region) => region.id === getValues().regionId)?.name
  }

  function getThemeName(): string | undefined {
    return regionThemes.find((theme: Theme) => theme.id === getValues().themeId)?.name
  }

  function getChapterThemeName(): string | undefined {
    return chapterThemes.find((theme: Theme) => theme.id === getValues().chapterThemeId)?.name
  }

  function isRegionDisabled(): boolean {
    return !hasLicenceSelected() || (getValues().chapterId ? getValues().chapterId === " " : true)
  }

  function isThemeDisabled(): boolean {
    return isRegionDisabled() || (getValues().regionId ? getValues().regionId === " " : true)
  }

  function isChapterThemeDisabled(): boolean {
    return isRegionDisabled()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box className="metadata-manager-form">
        <LicenceField
          name="licenceIds"
          control={control}
          required={t("commonFormfields.requiredLicence")}
          label={t("commonFormfields.licences")}
          className="licence-field"
          licences={licences}
        />
        <TextInputField
          className="title-field"
          name="title"
          control={control}
          required={t("metadata.formFields.requiredTitle")}
          label={t("metadata.formFields.metadataTitle")}
          placeholder={t("metadata.formFields.metadataTitle")}
          disabled={!hasLicenceSelected()}
          fullWidth
        />
        <TextInputField
          className="date-field"
          name="dateOfMap"
          control={control}
          label={t("metadata.formFields.date")}
          type="date"
          disabled={!hasLicenceSelected()}
          required={t("metadata.formFields.requiredDate")}
          fullWidth
        />
        <Box className="index-fields">
          <Header4>{t("metadata.formFields.indexStructure")}</Header4>
          <CheckboxControl
            name="showUnderChapter"
            control={control}
            label={t("metadata.formFields.showUnderChapter")}
            className="show-in-chapter-checkbox"
            disabled={isRegionDisabled()}
          />
          <TextInputField
            className="chapter-field"
            name="chapterId"
            control={control}
            required={t("commonFormfields.requiredChapter")}
            label={t("commonFormfields.chapter")}
            disabled={!hasLicenceSelected()}
            onSelect={chapterIdChanged}
            select
          >
            <MenuItem value=" ">{t("commonFormfields.noChapter")}</MenuItem>
            {chapters.map((chapter: Chapter, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <MenuItem key={index} value={chapter.id}>
                {chapter.name}
              </MenuItem>
            ))}
          </TextInputField>
          <TextInputField
            className="topic-chapter-field"
            name="topicChapterId"
            control={control}
            label={t("metadata.formFields.topicChapter")}
            disabled={!hasLicenceSelected()}
            validate={{
              differentFromChapter: (value) => validateTopicChapter(value)
            }}
            select
          >
            <MenuItem value=" ">{t("commonFormfields.noChapter")}</MenuItem>
            {chapters.map((chapter: Chapter, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <MenuItem key={index} value={chapter.id}>
                {chapter.name}
              </MenuItem>
            ))}
          </TextInputField>
          <CheckboxControl
            name="showUnderRegion"
            control={control}
            label={t("metadata.formFields.showBelowArea")}
            className="show-in-region-checkbox"
            disabled={isThemeDisabled()}
          />
          <TextInputField
            className="region-field"
            name="regionId"
            control={control}
            label={t("metadata.formFields.area")}
            disabled={isRegionDisabled()}
            onSelect={regionIdChanged}
            select
          >
            <MenuItem value=" ">{t("metadata.formFields.noArea")}</MenuItem>
            {regions.map((region: Region, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <MenuItem key={index} value={region.id}>
                {region.name}
              </MenuItem>
            ))}
          </TextInputField>
          <TextInputField
            className="region-theme-field"
            name="themeId"
            control={control}
            disabled={isThemeDisabled()}
            label={t("metadata.formFields.areaTheme")}
            select
          >
            <MenuItem value=" ">{t("metadata.formFields.noTheme")}</MenuItem>
            {regionThemes.map((theme: Theme, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <MenuItem key={index} value={theme.id}>
                {theme.name}
              </MenuItem>
            ))}
          </TextInputField>
          <TextInputField
            className="chapter-theme-field"
            name="chapterThemeId"
            control={control}
            disabled={isChapterThemeDisabled()}
            label={t("metadata.formFields.chapterTheme")}
            select
          >
            <MenuItem value=" ">{t("metadata.formFields.noTheme")}</MenuItem>
            {chapterThemes.map((theme: Theme, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <MenuItem key={index} value={theme.id}>
                {theme.name}
              </MenuItem>
            ))}
          </TextInputField>
          <Box className="index-preview">
            <MetadataIndexFormPreview
              chapter={getChapterName()}
              topicChapter={getTopicChapterName()}
              region={getRegionName()}
              theme={getThemeName()}
              chapterTheme={getChapterThemeName()}
              showUnderChapter={watch().showUnderChapter}
              showUnderRegion={watch().showUnderRegion}
            />
          </Box>
        </Box>
        {assets && (
          <Box className="asset-field">
            <Body1>
              <b>{t("metadata.formFields.mapAsset")}</b>
            </Body1>
            <Autocomplete
              options={assets}
              getOptionLabel={(option: Asset) => option.assetName}
              onChange={onAssetSelect}
              fullWidth
              onInputChange={onAssetInputChange}
              value={selectedAsset ? selectedAsset : null}
              className={"autocomplete-control"}
              disabled={!hasLicenceSelected()}
              renderInput={(params) => <TextField {...params} color="primary" variant="outlined" />}
            />
          </Box>
        )}
        <TextInputField
          name="pageNumber"
          control={control}
          label={t("metadata.formFields.page")}
          type="number"
          disabled={!hasLicenceSelected()}
          className="pageNumber-field"
        />
        <TextInputField
          name="pageSection"
          control={control}
          label={t("metadata.formFields.mapLetter")}
          disabled={!hasLicenceSelected()}
          className="pageSection-field"
        />
        <TextInputField
          className="alias-field"
          name="aliasses"
          control={control}
          required={t("metadata.formFields.requiredAliases")}
          disabled={!hasLicenceSelected()}
          label={t("metadata.formFields.aliases")}
          placeholder={t("metadata.formFields.exampleAliases")}
        />
        <TextInputField
          className="gen-alias-field"
          name="generatedAliasses"
          control={control}
          disabled={true}
          label={t("metadata.formFields.generatedAliases")}
        />
        {categories && (
          <Box className="category-field">
            <Body1>
              <b>{t("metadata.formFields.mapType")}</b>
            </Body1>
            <SelectorComponent
              value={getValues().category}
              onChange={(value: string) => setValue("category", value)}
              collection={categories}
            />
          </Box>
        )}
        <CheckboxControl
          className="only-digital-field"
          name="isDigitalOnly"
          control={control}
          label={t("metadata.formFields.isDigitalOnly")}
          disabled={!hasLicenceSelected()}
        />
        {selectedAsset && (
          <MapField
            open={mapDialogOpen}
            toggleOpen={() => setMapDialogOpen(!mapDialogOpen)}
            showControls={showControls}
            asset={selectedAsset}
            showAttachmentPanel={showAttachmentPanel}
            attachments={metadata.attachments}
            updateAttachments={updateAttachments}
            moveAttachment={moveAttachment}
            removePinLocation={removePinLocation}
            removeAttachment={removeAttachment}
          />
        )}

        <Button
          className="save-field"
          disabled={!hasLicenceSelected() || !hasValidIndexSpot}
          fullWidth
          type="submit"
          color="primary"
          variant="contained"
        >
          {t("save")}
        </Button>
        {selectedAsset && (
          <Link className="gotoAsset" target="_blank" href={`/atlas/manage/assets/${selectedAsset?.id}`}>
            <Button fullWidth variant="contained">
              {t("metadata.formFields.navigateAsset")}
            </Button>
          </Link>
        )}

        {metadata.id && (
          <Link className="gotoMap" target="_blank" href={`/atlas/map/${metadata?.id}`}>
            <Button fullWidth variant="contained">
              {t("metadata.formFields.navigateToMap")}
            </Button>
          </Link>
        )}
      </Box>
      <FormValidationErrors errors={errors} submitCount={submitCount} />
      {selectedAsset && (
        <MapAttachmentEditDialog
          mapId={selectedAsset.id as string}
          casperId={selectedAsset.assetId}
          projection={selectedAsset.useProjection ? selectedAsset.projection : "x"}
          corner={convertLatLngInterfaceToLatLng(
            (selectedAsset.useProjection ? selectedAsset.projectedCorner : selectedAsset.corner) as ILatLng
          )}
          oppositeCorner={convertLatLngInterfaceToLatLng(
            (selectedAsset.useProjection
              ? selectedAsset.projectedOppositeCorner
              : selectedAsset.oppositeCorner) as ILatLng
          )}
          attachmentCount={metadata.attachments.length}
          addAttachment={addAttachment}
          editAttachment={updateAttachment}
        />
      )}
    </form>
  )
}

function MapField({
  showControls,
  open,
  toggleOpen,
  asset,
  showAttachmentPanel,
  attachments,
  updateAttachments,
  moveAttachment,
  removePinLocation,
  removeAttachment
}: {
  showControls: boolean
  open: boolean
  toggleOpen: () => void
  asset: Asset
  showAttachmentPanel: boolean
  attachments: Attachment[]
  updateAttachments: (attachments: Attachment[]) => void
  moveAttachment: (attachment: Attachment) => void
  removePinLocation: (attachment: Attachment) => void
  removeAttachment: (attachment: Attachment) => void
}): ReactElement {
  const attachmentsWithLocation: Attachment[] = attachments.filter(
    (attachment: Attachment) => attachment.pointOnMap !== undefined && attachment.pointOnMap !== null
  )

  const { t } = useTranslation("contentManager")

  return (
    <>
      <Button className="map-editor-button-field" fullWidth variant="contained" onClick={toggleOpen}>
        {t("metadata.formFields.openAttachmentEditor")}
      </Button>
      <Dialog open={open} classes={{ paperWidthSm: "map-metadata-manager-dialog" }} id="app">
        <Button variant="contained" onClick={toggleOpen}>
          {t("metadata.formFields.close")}
        </Button>
        <Box className="map-viewer">
          {asset && (
            <ErrorBoundary>
              <MapViewer
                mapId={asset.assetId}
                casperId={asset.assetId}
                corner={convertLatLngInterfaceToLatLng(
                  (asset.useProjection ? asset.projectedCorner : asset.corner) as ILatLng
                )}
                oppositeCorner={convertLatLngInterfaceToLatLng(
                  (asset.useProjection ? asset.projectedOppositeCorner : asset.oppositeCorner) as ILatLng
                )}
                projection={asset.useProjection ? asset.projection : "x"}
              >
                <MapViewerControls
                  prefix={asset.assetId}
                  corner={convertLatLngInterfaceToLatLng(
                    (asset.useProjection ? asset.projectedCorner : asset.corner) as ILatLng
                  )}
                  oppositeCorner={convertLatLngInterfaceToLatLng(
                    (asset.useProjection ? asset.projectedOppositeCorner : asset.oppositeCorner) as ILatLng
                  )}
                  options={{
                    drawingControls: false,
                    mapActions: false,
                    visible: showControls
                  }}
                />
                {showControls && (
                  <MapActionsPanel
                    attachmentCount={attachments.length}
                    options={{
                      comparisons: false,
                      drawing: false,
                      googleMaps: false
                    }}
                  />
                )}
                <MapDrawing
                  removePinLocation={removePinLocation}
                  drawings={[]}
                  attachments={attachmentsWithLocation}
                  showDrawings={false}
                />
                {!showControls && <AttachmentLocationChanger onSave={moveAttachment} />}
              </MapViewer>
            </ErrorBoundary>
          )}
        </Box>
        {showAttachmentPanel && showControls && (
          <Box className="attachment-panel">
            <MapAttachementSidePanel
              allowAuthorContentEditing={true}
              attachments={attachments}
              reorderAttachments={updateAttachments}
              onDeleteAttachment={removeAttachment}
            />
          </Box>
        )}
      </Dialog>
    </>
  )
}

function mapStateToProps(state: AppState) {
  return {
    showControls: state.attachmentsEditor.currentAttachment === undefined,
    showAttachmentPanel: state.mapViewer.panels.get(MapViewerPanel.Attachement) || false,
    categories: state.metadata.categories
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MetadataActions>) {
  return {
    addAttachment: (attachment: Attachment, file?: File) => dispatch(metadataAddAttachment(attachment, file)),
    updateAttachment: (attachment: Attachment, file?: File) => dispatch(metadataUpdateAttachment(attachment, file)),
    updateAttachments: (attachments: Attachment[]) => dispatch(metadataUpdateAttachments(attachments)),
    moveAttachment: (attachment: Attachment) => dispatch(metadataMoveAttachment(attachment)),
    searchAssets: (searchValue: string) => dispatch(searchAssets(searchValue)),
    fetchCategories: () => dispatch(metadataFetchCategories())
  }
}

export const MapMetadataManagerForm = connect(mapStateToProps, mapDispatchToProps)(MapMetadataManagerFormView)
